/**
 * Records this side of a call for the compiled recording
 * (docs/call-recording.md). On by default in every call; anyone allowed
 * can turn it off and on again for everyone, and off time is cut from the
 * file.
 *
 * While the call is live and recording is on, one MediaRecorder runs per
 * track this side contributes (segmentsWanted): its own camera and mic,
 * the call's sound as it hears it (coach and player), the match as the
 * player's screen shows it, and a screen share. Each recorder's run is a
 * segment, placed on the server's clock by its manifest, cut into 4 second
 * chunks that go to the upload queue (services/callRecording). A segment
 * ends and a new one starts when its tracks change, so the compile job can
 * place every piece by time alone.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  TIMESLICE_MS, bitrates, chunkPath, clockOffset, containRect, manifestPath, pickMimeType, segmentId,
  segmentsWanted, stageSize, canToggleRecording, type SegmentKind, type SegmentManifest,
} from '../utils/callRecordingPlan';
import {
  enqueueUpload, enterRecording, joinRecording, leaveRecording, sampleServerClock, setRecordingEnabled,
  subscribeRecording,
} from '../services/callRecording';

interface Clock { offsetMs: number; rttMs: number | null }

/** One MediaRecorder run: its manifest, its chunks in order, then the manifest again with its end. */
class SegmentRecorder {
  readonly done: Promise<void>;
  private rec: MediaRecorder;
  private chain: Promise<void> = Promise.resolve();

  constructor(recordingId: string, uid: string, kind: SegmentKind, stream: MediaStream, mimeType: string,
    phone: boolean, clock: Clock, onFail: () => void) {
    const bps = bitrates(kind, phone);
    this.rec = new MediaRecorder(stream, {
      mimeType,
      ...(bps.video ? { videoBitsPerSecond: bps.video } : {}),
      audioBitsPerSecond: bps.audio,
    });
    const contentType = mimeType.split(';')[0];
    let manifest: SegmentManifest | null = null;
    let seq = 0;
    const put = (path: string, blob: Blob, type: string, replace: boolean) => {
      this.chain = this.chain.then(() => enqueueUpload({ recordingId, uid, path, contentType: type, blob, replace }));
    };
    const putManifest = () => {
      if (!manifest) return;
      put(manifestPath(recordingId, uid, manifest.segmentId),
        new Blob([JSON.stringify(manifest)], { type: 'application/json' }), 'application/json', true);
    };
    this.rec.onstart = () => {
      const startedAtServerMs = Date.now() + clock.offsetMs;
      manifest = {
        uid, kind, startedAtServerMs, endedAtServerMs: null, rttMs: clock.rttMs, mimeType, chunkCount: 0,
        segmentId: segmentId(kind, startedAtServerMs, Math.random().toString(36).slice(2)),
      };
      putManifest();
    };
    this.rec.ondataavailable = (e) => {
      if (!manifest || e.data.size === 0) return;
      put(chunkPath(recordingId, uid, manifest.segmentId, seq++), e.data, contentType, false);
      manifest.chunkCount = seq;
    };
    this.rec.onerror = () => { this.stop(); onFail(); };
    this.done = new Promise<void>((resolve) => {
      this.rec.onstop = () => {
        if (manifest) {
          manifest.endedAtServerMs = Date.now() + clock.offsetMs;
          putManifest();
        }
        void this.chain.then(resolve, resolve);
      };
    });
    this.rec.start(TIMESLICE_MS);
  }

  get stopped(): boolean {
    return this.rec.state === 'inactive';
  }

  stop() {
    if (this.rec.state !== 'inactive') {
      try { this.rec.stop(); } catch { /* already stopping */ }
    }
  }
}

/** The call's sound as this side hears it: its own mic and everyone else's voice, mixed. */
function soundMix(tracks: MediaStreamTrack[]): { stream: MediaStream; close: () => void } {
  const ctx = new AudioContext();
  void ctx.resume().catch(() => { /* runs once the page is touched */ });
  const dest = ctx.createMediaStreamDestination();
  for (const t of tracks) ctx.createMediaStreamSource(new MediaStream([t])).connect(dest);
  return { stream: dest.stream, close: () => { void ctx.close().catch(() => {}); } };
}

/**
 * The match as the player sees it: every video in the match's frame
 * ([data-match-host]) drawn where it sits, whole, with the coach's strokes
 * over it, on a fixed 16:9 canvas. A video that would taint the canvas (no
 * CORS on its source) is left out rather than stopping the recording.
 */
function stageCompositor(phone: boolean): { stream: MediaStream; close: () => void } {
  const { w, h, fps } = stageSize(phone);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const g = canvas.getContext('2d')!;
  const readable = new Map<string, boolean>();
  const strokes = new Map<string, HTMLImageElement>();

  const canRead = (v: HTMLVideoElement): boolean => {
    if (v.srcObject) return true;
    const key = v.currentSrc;
    if (!key) return false;
    const known = readable.get(key);
    if (known !== undefined) return known;
    // A fresh canvas each probe: a tainted one stays tainted.
    const probe = document.createElement('canvas');
    probe.width = probe.height = 1;
    const pg = probe.getContext('2d', { willReadFrequently: true })!;
    let ok = false;
    try {
      pg.drawImage(v, 0, 0, 1, 1);
      pg.getImageData(0, 0, 1, 1);
      ok = true;
    } catch { ok = false; }
    readable.set(key, ok);
    return ok;
  };

  const strokeImage = (svg: SVGSVGElement, rw: number, rh: number): HTMLImageElement | null => {
    if (!svg.querySelector('polyline, path, line')) return null;
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    clone.setAttribute('width', String(Math.round(rw)));
    clone.setAttribute('height', String(Math.round(rh)));
    const markup = new XMLSerializer().serializeToString(clone);
    const hit = strokes.get(markup);
    if (hit) return hit.complete ? hit : null;
    if (strokes.size > 40) strokes.clear();
    const img = new Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
    strokes.set(markup, img);
    return null;
  };

  const draw = () => {
    g.fillStyle = '#000';
    g.fillRect(0, 0, w, h);
    const host = document.querySelector<HTMLElement>('[data-match-host]');
    if (!host) return;
    const hr = host.getBoundingClientRect();
    const area = containRect(hr.width, hr.height, { x: 0, y: 0, w, h });
    if (!area) return;
    const s = area.w / hr.width;
    const place = (r: DOMRect) => ({ x: area.x + (r.left - hr.left) * s, y: area.y + (r.top - hr.top) * s, w: r.width * s, h: r.height * s });
    g.save();
    g.beginPath();
    g.rect(area.x, area.y, area.w, area.h);
    g.clip();
    host.querySelectorAll('video').forEach((v) => {
      if (v.readyState < 2 || !v.videoWidth) return;
      const r = v.getBoundingClientRect();
      if (!r.width || !r.height || !canRead(v)) return;
      const cell = place(r);
      const box = containRect(v.videoWidth, v.videoHeight, cell);
      if (!box) return;
      g.save();
      g.beginPath();
      g.rect(cell.x, cell.y, cell.w, cell.h);
      g.clip();
      try { g.drawImage(v, box.x, box.y, box.w, box.h); } catch { /* a frame not ready */ }
      g.restore();
    });
    host.querySelectorAll<SVGSVGElement>('[data-match-draw-layer] svg').forEach((svg) => {
      const r = svg.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const img = strokeImage(svg, r.width, r.height);
      if (!img) return;
      const cell = place(r);
      try { g.drawImage(img, cell.x, cell.y, cell.w, cell.h); } catch { /* an image still decoding */ }
    });
    g.restore();
  };
  draw();
  const id = setInterval(draw, Math.round(1000 / fps));
  const stream = canvas.captureStream(fps);
  return {
    stream,
    close: () => {
      clearInterval(id);
      stream.getTracks().forEach((t) => t.stop());
    },
  };
}

/** Recorder errors in one call before this side stops trying. */
const MAX_FAILS = 5;

const live = (t: MediaStreamTrack) => t.readyState === 'live';

export interface CallRecordingState {
  /** Recording is on for this call (the recording document's say). */
  enabled: boolean;
  /** This call has a recording this side is part of. */
  active: boolean;
  /** This side may turn it off and on. */
  canToggle: boolean;
  /** A toggle is on its way. */
  busy: boolean;
  /** This browser can record (webm MediaRecorder). */
  supported: boolean;
  toggle: () => void;
}

export function useCallRecording(p: {
  roomId: string;
  /** The signed-in user; null (a guest with no account) records nothing. */
  uid: string | null;
  inCall: boolean;
  localStream: MediaStream | null;
  /** Every other participant's streams; their audio goes in the sound mix. */
  remoteStreams: MediaStream[];
  /** This side's screen share, while sharing. */
  shareStream: MediaStream | null;
  /** The match is open on this side. */
  matchOpen: boolean;
  phone: boolean;
}): CallRecordingState {
  const { roomId, uid, inCall, localStream, remoteStreams, shareStream, matchOpen, phone } = p;
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [role, setRole] = useState<'coach' | 'player' | 'guest' | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [kind, setKind] = useState<'lesson' | 'call' | null>(null);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState<Clock | null>(null);
  // Bumped to join again: a status that left 'recording' while still in the call.
  const [joinGen, setJoinGen] = useState(0);
  const [failGen, setFailGen] = useState(0);

  const supported = useMemo(() => typeof MediaRecorder !== 'undefined'
    && !!pickMimeType((t) => MediaRecorder.isTypeSupported(t), true), []);

  // Join while in the call, retrying while the call has no session yet.
  useEffect(() => {
    if (!inCall || !uid) return;
    const me = uid;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let joined: string | null = null;
    const attempt = async (delay: number) => {
      try {
        const [j, samples] = await Promise.all([joinRecording(roomId), sampleServerClock()]);
        if (!alive) return;
        if (!j.recordingId) { timer = setTimeout(() => void attempt(Math.min(delay * 2, 30_000)), delay); return; }
        const c = clockOffset(samples);
        joined = j.recordingId;
        enterRecording(j.recordingId, me);
        setClock(c ?? { offsetMs: j.serverNowMs - Date.now(), rttMs: null });
        setRole(j.role);
        setEnabled(j.enabled);
        setRecordingId(j.recordingId);
      } catch {
        if (alive) timer = setTimeout(() => void attempt(Math.min(delay * 2, 30_000)), delay);
      }
    };
    void attempt(2000);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      if (joined) {
        // The recorders stop first (below); this waits for their last chunks.
        const rid = joined;
        void stopAll().then(() => leaveRecording(rid));
      }
      setRecordingId(null);
      setClock(null);
    };
  }, [inCall, roomId, uid, joinGen]);

  // The recording's switch, and whether it is still taking tracks.
  useEffect(() => {
    if (!recordingId) return;
    return subscribeRecording(recordingId, (r) => {
      if (!r) return;
      setEnabled(r.enabled);
      setKind(r.kind === 'lesson' ? 'lesson' : 'call');
      if (r.status !== 'recording') setJoinGen((n) => n + 1);
    });
  }, [recordingId]);

  // The recorders, one per kind, each with the tracks it was started on.
  const running = useRef(new Map<SegmentKind, { sig: string; rec: SegmentRecorder; close?: () => void }>());
  const stopKind = useCallback((k: SegmentKind): Promise<void> => {
    const r = running.current.get(k);
    if (!r) return Promise.resolve();
    running.current.delete(k);
    r.rec.stop();
    return r.rec.done.finally(() => r.close?.());
  }, []);
  const stopAllRef = useRef<() => Promise<void>>(() => Promise.resolve());
  stopAllRef.current = () => Promise.all([...running.current.keys()].map(stopKind)).then(() => {});
  const stopAll = () => stopAllRef.current();

  const localTracks = (localStream?.getTracks() ?? []).filter(live);
  const remoteAudio = remoteStreams.flatMap((s) => s.getAudioTracks()).filter(live);
  const shareTracks = (shareStream?.getVideoTracks() ?? []).filter(live);
  const camSig = localTracks.map((t) => t.id).sort().join(',');
  const mixSig = [...localTracks.filter((t) => t.kind === 'audio'), ...remoteAudio].map((t) => t.id).sort().join(',');
  const shareSig = shareTracks.map((t) => t.id).join(',');

  useEffect(() => {
    const on = inCall && supported && !!uid && !!recordingId && !!clock && enabled && !!role;
    const want = on ? segmentsWanted({
      role: role!, hasCamOrMic: localTracks.length > 0, matchOpen, sharing: shareTracks.length > 0,
    }) : [];
    const sigs: Record<SegmentKind, string> = { cam: camSig, mix: mixSig, stage: phone ? 'phone' : 'large', share: shareSig };
    for (const k of [...running.current.keys()]) {
      const r = running.current.get(k)!;
      if (!want.includes(k) || r.sig !== sigs[k] || r.rec.stopped) void stopKind(k);
    }
    // A recorder that keeps failing is left off rather than restarted for ever.
    if (!on || failGen > MAX_FAILS) return;
    const isSupported = (t: string) => MediaRecorder.isTypeSupported(t);
    for (const k of want) {
      if (running.current.has(k)) continue;
      let stream: MediaStream;
      let close: (() => void) | undefined;
      if (k === 'cam') stream = new MediaStream(localTracks);
      else if (k === 'share') stream = new MediaStream(shareTracks);
      else if (k === 'mix') {
        const audio = [...localTracks.filter((t) => t.kind === 'audio'), ...remoteAudio];
        if (audio.length === 0) continue;
        const m = soundMix(audio);
        stream = m.stream;
        close = m.close;
      } else {
        const c = stageCompositor(phone);
        stream = c.stream;
        close = c.close;
      }
      const video = stream.getVideoTracks().length > 0;
      const mime = pickMimeType(isSupported, video);
      if (!mime) { close?.(); continue; }
      try {
        const rec = new SegmentRecorder(recordingId!, uid!, k, stream, mime, phone, clock!,
          () => setFailGen((n) => n + 1));
        running.current.set(k, { sig: sigs[k], rec, close });
      } catch {
        close?.();
      }
    }
    // The signatures stand for the tracks; the track arrays are rebuilt each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inCall, supported, recordingId, clock, enabled, role, matchOpen, phone, camSig, mixSig, shareSig, uid, failGen, stopKind]);

  // Gone from the page: every recorder stops and the last chunks are queued.
  useEffect(() => () => { void stopAllRef.current(); }, []);

  const canToggle = !!recordingId && canToggleRecording({ lesson: kind === 'lesson', role });
  const toggle = useCallback(() => {
    if (!recordingId || busy) return;
    const next = !enabled;
    setBusy(true);
    setEnabled(next);
    setRecordingEnabled(recordingId, next)
      .catch(() => setEnabled(!next))
      .finally(() => setBusy(false));
  }, [recordingId, busy, enabled]);

  return { enabled, active: !!recordingId, canToggle, busy, supported, toggle };
}
